import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../environments/environment';
import { catchError, map, take, tap } from 'rxjs/operators';
import { RoleCode } from './role';

export interface LoggedInUser {
  roles: RoleCode[];
  effectiveRoles?: RoleCode[];
  username: string;
  firstName?: string;
  lastName?: string;
  userId: string;
}

@Injectable({
  providedIn: 'root',
})
export class UserService {
  private userSubject$ = new BehaviorSubject<LoggedInUser>(JSON.parse(localStorage.getItem('currentUser')));
  readonly user$ = this.userSubject$.asObservable();

  getEffectiveRoles(user: LoggedInUser | null = this.currentUserValue): RoleCode[] {
    if (!user) return [];
    return user.effectiveRoles?.length ? user.effectiveRoles : (user.roles ?? []);
  }

  readonly isAdmin$ = this.user$.pipe(
    map((user) => this.getEffectiveRoles(user).includes(RoleCode.Administrator)),
  );
  readonly isConsultant$ = this.user$.pipe(
    map((user) => this.getEffectiveRoles(user).includes(RoleCode.Consultant)),
  );
  readonly isManager$ = this.user$.pipe(
    map((user) => this.getEffectiveRoles(user).includes(RoleCode.Manager)),
  );
  readonly isFinancial$ = this.user$.pipe(
    map((user) => this.getEffectiveRoles(user).includes(RoleCode.Financial)),
  );
  readonly isSuperAdmin$ = this.user$.pipe(
    map((user) => this.getEffectiveRoles(user).includes(RoleCode.SuperAdmin)),
  );

  readonly canAssignRestrictedRoles$ = this.isSuperAdmin$;

  public get currentUserValue(): LoggedInUser | null {
    return this.userSubject$.getValue();
  }

  constructor(private http: HttpClient) {}

  login(username: string, password: string): Observable<LoggedInUser> {
    return this.http.post<LoggedInUser>(
      `${environment.api}/login`,
      { username, password },
      { withCredentials: true }
    ).pipe(
      tap((user) => {
        this.userSubject$.next(user);
        localStorage.setItem('currentUser', JSON.stringify(user));
      }),
    );
  }

  refreshUser(): Observable<LoggedInUser> {
    return this.http.get<LoggedInUser>(`${ environment.api }/status`, { withCredentials: true }).pipe(
      take(1),
      tap((user) => {
        this.userSubject$.next(user);
        localStorage.setItem('currentUser', JSON.stringify(user));
      }),
    );
  }

  reloadUser(): void {
    this.refreshUser().subscribe();
  }

  logout(): Observable<{}> {
    return this.http.post<{}>(`${ environment.api }/logout`, {}, { withCredentials: true }).pipe(
      catchError(() => {
        this.userSubject$.next(null);
        return of({});
      }),
      tap(() => this.userSubject$.next(null)),
    );
  }
}
